/**
 * useScreenWithOrchestrator Hook
 *
 * Integration layer combining useScreenState with ActionOrchestrator.
 * Screens using this hook MUST implement a load() method that uses execute().
 *
 * @example
 * ```typescript
 * const screen = useScreenWithOrchestrator({
 *   metadata: { screenType: ScreenType.BOARD, title: 'Board', requiresAuth: true },
 *   onLoad: async (strategy, { setLoading, setSuccess, setError }) => {
 *     setLoading();
 *     const result = await screen.execute(Action.GET_BOARD, { boardId });
 *     if (result) {
 *       setSuccess();
 *     }
 *   }
 * });
 * ```
 */

import { useCallback, useRef } from 'react';
import { Action } from '@/business/types/action.types';
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { getResponseCodeMessage } from '@/business/constants/response-codes';
import { ResponseCode } from '@/business/types/response-codes.types';
import { logger } from '@/utils/logger';
import { useScreenState, UseScreenStateOptions } from './useScreenState';
import {
  ScreenStateHook,
  ScreenError,
  LoadStrategy,
} from '@/types/screen.types';

/**
 * Extended screen state hook with orchestrator integration
 */
export interface ScreenWithOrchestratorHook<
  TOutput = unknown,
> extends ScreenStateHook {
  /**
   * Execute an action through the orchestrator.
   * Automatically manages screen state based on result.
   *
   * @param action The action to execute
   * @param data Input data for the action
   * @param callbacks Optional callbacks for success/error
   * @returns The result data (if successful)
   */
  execute: <TInput = unknown>(
    action: Action,
    data: TInput,
    callbacks?: {
      onSuccess?: (data: TOutput) => void;
      onError?: (error: ScreenError) => void;
    }
  ) => Promise<TOutput | undefined>;
}

/**
 * Options for screens using orchestrator pattern.
 * The screen MUST provide an onLoad implementation.
 */
export interface UseScreenWithOrchestratorOptions extends Omit<
  UseScreenStateOptions,
  'onLoad'
> {
  /**
   * Optional: Screen's load implementation using execute().
   * If not provided, screen must manually call execute() to load data.
   *
   * @example
   * onLoad: async (strategy, screenState) => {
   *   const data = await screen.execute(Action.GET_BOARD, { boardId });
   *   if (data) screenState.setSuccess();
   * }
   */
  onLoad?: UseScreenStateOptions['onLoad'];
}

/**
 * Custom hook that combines screen state management with action orchestrator.
 *
 * Features:
 * - Automatic state transitions based on orchestrator results
 * - Maps response codes to screen errors
 * - Handles retry logic for server errors
 * - Provides unified execute() function
 * - Exposes load() and reload() methods
 *
 * @param options Screen configuration options
 * @returns Extended screen state with execute function
 */
export function useScreenWithOrchestrator<TOutput = unknown>(
  options: UseScreenWithOrchestratorOptions
): ScreenWithOrchestratorHook<TOutput> {
  // Store execute function reference to use in onLoad
  const executeRef = useRef<any>(null);

  // Stabilize metadata reference to prevent infinite re-renders
  const metadataRef = useRef(options.metadata);
  metadataRef.current = options.metadata;

  // Create default onLoad if not provided
  const defaultOnLoad: UseScreenStateOptions['onLoad'] = async (
    _strategy: LoadStrategy,
    screenState
  ) => {
    // If no custom onLoad provided, just set to idle state
    // Screen must manually call execute() to load data
    screenState.setIdle();
    logger.info('Screen initialized without auto-load', {
      screen: metadataRef.current.title,
    });
  };

  // Use screen state with provided or default onLoad
  const screen = useScreenState({
    ...options,
    onLoad: options.onLoad || defaultOnLoad,
  });

  /**
   * Execute an action through the orchestrator with automatic state management
   */
  const execute = useCallback(
    async <TInput = unknown,>(
      action: Action,
      data: TInput,
      callbacks?: {
        onSuccess?: (data: TOutput) => void;
        onError?: (error: ScreenError) => void;
      }
    ): Promise<TOutput | undefined> => {
      // Set loading state
      screen.setLoading();

      logger.info('Executing action via screen orchestrator', {
        action,
        screen: metadataRef.current.title,
      });

      try {
        // Execute through orchestrator with progress tracking
        const result = await actionOrchestrator.execute<TInput, TOutput>(
          action,
          data,
          {
            onProgress: progressValue => {
              screen.setInProgress(progressValue);
            },
          }
        );

        // Check if response is successful (SUCCESS, CREATED, ACCEPTED, NO_CONTENT)
        const isSuccess =
          result.code === ResponseCode.SUCCESS ||
          result.code === ResponseCode.CREATED ||
          result.code === ResponseCode.ACCEPTED ||
          result.code === ResponseCode.NO_CONTENT;

        if (isSuccess) {
          // Success path
          screen.setSuccess();

          logger.info('Action succeeded', {
            action,
            code: result.code,
            screen: metadataRef.current.title,
          });

          // Call success callback
          if (callbacks?.onSuccess && result.data) {
            callbacks.onSuccess(result.data);
          }

          return result.data;
        } else {
          // Error path - create screen error
          const message = getResponseCodeMessage(result.code);

          // Server errors are retryable
          const canRetry =
            result.code === ResponseCode.INTERNAL_ERROR ||
            result.code === ResponseCode.SERVICE_UNAVAILABLE ||
            result.code === ResponseCode.GATEWAY_TIMEOUT ||
            result.code === ResponseCode.OPERATION_FAILED ||
            result.code === ResponseCode.NETWORK_ERROR ||
            result.code === ResponseCode.TIMEOUT_ERROR;

          const screenError: ScreenError = {
            code: result.code,
            message,
            canRetry,
            retryAction: canRetry
              ? () => execute(action, data, callbacks)
              : undefined,
          };

          screen.setError(screenError);

          logger.warn('Action failed', {
            action,
            code: result.code,
            message,
            canRetry,
            screen: metadataRef.current.title,
          });

          // Call error callback
          if (callbacks?.onError) {
            callbacks.onError(screenError);
          }

          return undefined;
        }
      } catch (err) {
        // Unexpected error (should not happen if orchestrator follows contract)
        logger.error('Unexpected error during action execution', err, {
          action,
          screen: metadataRef.current.title,
        });

        const screenError: ScreenError = {
          code: ResponseCode.INTERNAL_ERROR,
          message: 'An unexpected error occurred. Please try again.',
          canRetry: true,
          retryAction: () => execute(action, data, callbacks),
        };

        screen.setError(screenError);

        // Call error callback
        if (callbacks?.onError) {
          callbacks.onError(screenError);
        }

        return undefined;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screen]
  );

  // Store execute reference for use in onLoad
  executeRef.current = execute;

  return {
    ...screen,
    execute,
  };
}
